import React, { useState, useEffect } from "react";
import { aboutContent, aboutFeatures } from "../mock";
import { Check } from "lucide-react";

const AboutSection = () => {
  const images = [
    "/gym1.jpeg",
    "/gym2.jpeg",
    "/gym3.jpeg",
    "/gym4.jpeg",
  ];

  const [current, setCurrent] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  // ✅ Auto Slide (6 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [images.length]);

  // ✅ Touch Handlers
  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;

    if (distance > 50) {
      // Swipe Left → Next
      setCurrent((prev) => (prev + 1) % images.length);
    }

    if (distance < -50) {
      // Swipe Right → Previous
      setCurrent((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
    }

    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <section id="about" className="py-20 bg-gray-950 scroll-mt-24">
      <div className="container mx-auto px-6">

        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-4 uppercase tracking-tight">
            {aboutContent.title}
          </h2>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* 🔥 Left: Sliding Carousel with Swipe */}
          <div
            className="relative group overflow-hidden rounded-2xl shadow-2xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out will-change-transform"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt="Gym Interior"
                  className="w-full h-[500px] object-cover flex-shrink-0"
                />
              ))}
            </div>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            {/* Smaller Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <div
                  key={index}
                  onClick={() => setCurrent(index)}
                  className={`w-2 h-2 rounded-full cursor-pointer transition-all ${
                    current === index
                      ? "bg-brand-primary scale-125"
                      : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-6">
            <p className="text-lg text-gray-300 leading-relaxed">
              {aboutContent.description}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              {aboutContent.mission}
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
              {aboutFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center gap-3 bg-gray-900 p-4 rounded-xl hover:bg-gray-800 transition-colors duration-300 group"
                >
                  <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Check className="text-black" size={18} strokeWidth={3} />
                  </div>
                  <span className="text-white font-semibold">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-6">
              <button
                onClick={() => {
                  const contactSection = document.querySelector("#contact");
                  if (contactSection)
                    contactSection.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-auto bg-[#5FAE66] text-black px-8 py-4 rounded-full font-bold text-lg 
                           hover:bg-[#4e9c56] hover:scale-105 
                           transition-all duration-300 shadow-lg"
              >
                Start Your Journey
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;
